import React, {useEffect} from 'react';
import Constants, {AnnotationColors} from '../../constants/Constants';
import {PopoverButton, TscButtonType, TscPopoverOrigin, Glyphs, TscStyles, TscThemeName, TscThemeNames} from '@techsmith/tsc-cloud-style-guide';
import ColorSwatch from '../../static/svg/ColorSwatch.svg';
import {useTranslation} from 'react-i18next';
import styled from 'styled-components';
import {canvasStore} from '../../context/canvasProvider';
import withMemoizedContexts from '../../context/contextContainerHoC';
import {annotationManagerStore} from '../../context/annotationManagerProvider';
import {disableConversationStore} from '../../context/disableConversationProvider';
import {useTscTheme} from '../../context/themeProvider';

const ColorPickerWrapper = styled.div<{color: AnnotationColors; theme: TscThemeNames}>`
   margin-right: .375rem;

   svg:first-child {
      stroke-width: 2;
      stroke: ${props => props.theme === TscThemeName.dusk ? TscStyles.color.text.light : TscStyles.color.text.mediumDark};
      fill: ${props => props.color};
   }
`;

const ColorPickerGlyph = styled.div`
   display: flex;
   align-items: center;
   justify-content: center;

   svg:first-child {
      height: 1.125rem;
      width: 1.25rem;
      padding-left: 0.25rem;
   }

   svg:last-child {
      width: 1rem;
   }
`;

export const ColorPickerColors = styled.div`
   display: flex;
   padding: 0.625rem .75rem;

   button:first-of-type {
      margin: 0;
   }

   button {
      border: 2px solid black;
      border-radius: ${TscStyles.border.radius.lg};
      height: 1.25rem;
      width: 1.25rem;

      &:hover {
         cursor: pointer;
         box-shadow: 0 0.3rem 0.5rem rgba(0, 0, 0, 0.5);
      }

      &:focus {
         outline: 1px dashed;
         outline-offset: 2px;
      }
   }
`;

export const ColorPickerBase: React.FC<IColorPickerProps & IStateMappedProps> = ({onColorChange, canvasRef, annotationColor, isAnnotatingDisabled}) => {
   const {t} = useTranslation();
   const theme = useTscTheme();

   useEffect(() => {
      if (!annotationColor) {
         onColorChange(Constants.annotation.defaultColor);
      }
   }, []);

   const getColorText = (color: AnnotationColors): string => {
      const colorValue = color.toLowerCase();
      switch (colorValue) {
         case AnnotationColors.yellow: {
            return t('annotation.colors.yellow');
         } case AnnotationColors.blue: {
            return t('annotation.colors.blue');
         } case AnnotationColors.green: {
            return t('annotation.colors.green');
         } case AnnotationColors.red: {
            return t('annotation.colors.red');
         } case AnnotationColors.white: {
            return t('annotation.colors.white');
         } default: {
            return colorValue;
         }
      }
   };

   const colorPickerGlyph = (
      <ColorPickerGlyph>
         <ColorSwatch />
         <Glyphs.CaratDown16x16 />
      </ColorPickerGlyph>
   );

   const pickerPopover = (
      <ColorPickerColors>
         {Constants.annotation.colors.map((color: AnnotationColors) => (
            <button
               key={color}
               title={getColorText(color)}
               style={{background: color}}
               onClick={() => onColorChange(color)}
               data-test-id={`color-picker-${color}`}
            />
         ))}
      </ColorPickerColors>
   );

   return (
      <ColorPickerWrapper
         color={annotationColor}
         theme={theme}
      >
         <PopoverButton
            buttonType={TscButtonType.secondary}
            buttonTitle={t('annotation.colorSelectionAriaLabel', {color: getColorText(annotationColor)})}
            disabled={!canvasRef || isAnnotatingDisabled}
            origin={TscPopoverOrigin.bottomRight}
            popoverMenu={pickerPopover}
            themeName={theme}
            closeMenuOnClick={true}
            buttonGlyph={colorPickerGlyph}
            useMobileMenu={false}
            testId="color-picker"
         />
      </ColorPickerWrapper>
   );
};

export interface IColorPickerProps {
   onColorChange(color: AnnotationColors): void;
}

export interface IStateMappedProps {
   canvasRef: any;
   annotationColor: AnnotationColors;
   isAnnotatingDisabled: boolean;
}

const mapStatesToProps = (
   {onColorChange}: IColorPickerProps,
   {canvasRef}: Partial<IStateMappedProps>,
   {annotationColor}: Partial<IStateMappedProps>,
   {isAnnotatingDisabled}: Partial<IStateMappedProps>
): IColorPickerProps & IStateMappedProps => ({onColorChange, canvasRef, annotationColor, isAnnotatingDisabled});

export default withMemoizedContexts(mapStatesToProps, canvasStore, annotationManagerStore, disableConversationStore)(ColorPickerBase);
